'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { ShoppingCart, Handshake, Star } from 'lucide-react';
import Link from 'next/link';
import { useStoreUI } from '@/lib/store/use-store-ui';
import { useCartStore } from '@/lib/store/cart-store';
import { formatSlug } from '@/lib/utils';
import { cn, getItemPrice } from '@/lib/utils';
import { ArtworkOfferModal } from './artwork-offer-modal';

interface ProductCardProps {
  product: {
    id: string;
    name: string;
    type?: string;
    subItemType?: string;
    price?: number;
    collection?: string;
    dimensions?: {
      width: number;
      height: number;
    };
    size?: string;
    media?: {
      main?: string;
      thumb?: string;
    };
    description?: string;
  };
}

export default function ProductCard({ product }: ProductCardProps) {
  const addItem = useCartStore((state) => state.addItem);
  const [isHovered, setIsHovered] = useState(false);
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [imageAspectRatio, setImageAspectRatio] = useState<string | null>(null);

  const imageUrl = product.media?.main || product.media?.thumb || '';
  const hasImage = Boolean(imageUrl);
  const { layoutMode, zoomLevel } = useStoreUI();
  const price = getItemPrice(product);

  const isList = layoutMode === 'list';
  const isCompact = zoomLevel === -1;
  const isLarge = zoomLevel === 1;
  const isMasonry = layoutMode === 'masonry';

  const hasDimensions = Boolean(product.dimensions?.width && product.dimensions?.height);
  const safeAspectRatio = hasDimensions
    ? `${product.dimensions?.width} / ${product.dimensions?.height}`
    : imageAspectRatio || '1 / 1'; // Fallback to detected image format or square

  const handleAddToCart = () => {
    addItem({
      productId: product.id,
      name: product.name,
      price: price || 0,
      quantity: 1,
      image: imageUrl,
    });
  };

  const handleAcceptOffer = (amount: number) => {
    addItem({
      productId: product.id,
      name: product.name,
      price: amount,
      quantity: 1,
      image: imageUrl,
    });
    setIsOfferModalOpen(false);
  };

  return (
    <motion.div
      layout
      className={cn(
        "group relative bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg hover:shadow-cyan-500/10 transition-all duration-300 border border-white/10",
        isList
          ? isCompact
            ? "flex flex-row h-32"
            : "flex flex-row h-32 sm:h-40 lg:h-48"
          : "flex flex-col",
        isMasonry && "break-inside-avoid mb-6 sm:mb-8"
      )}
      whileHover={{
        y: -4,
        borderColor: "rgba(6, 182, 212, 0.3)",
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 25,
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      {/* Product Image */}
      <Link
        href={`/store/${product.id}`}
        className={cn(
          "block relative overflow-hidden group",
          !isMasonry && "bg-muted/20",
          isList
            ? isCompact
              ? "w-32 h-full shrink-0"
              : "w-32 sm:w-48 lg:w-64 h-full shrink-0"
            : isMasonry
              ? "w-full"
              : "w-full p-4 aspect-square"
        )}
        style={isMasonry ? { aspectRatio: safeAspectRatio } : {}}
      >
        {hasImage ? (
          <motion.div
            layout
            className="relative w-full h-full"
            animate={{
              scale: isHovered ? 1.05 : 1,
            }}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 22,
              mass: 1.2
            }}
          >
            <Image
              src={imageUrl}
              alt={product.name}
              fill
              className={cn(
                "w-full h-full transition-all duration-500",
                isMasonry ? "object-cover" : "object-contain p-2"
              )}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
              onLoadingComplete={({ naturalWidth, naturalHeight }) => {
                if (!hasDimensions) {
                  setImageAspectRatio(`${naturalWidth} / ${naturalHeight}`);
                }
              }}
            />
          </motion.div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-white/5">
            <div className="text-muted-foreground/30 text-center">
              <Star className="w-8 h-8 mx-auto mb-2 opacity-20" />
            </div>
          </div>
        )}
      </Link>

      {/* Product Info */}
      <div className={cn(
        isCompact ? "p-3" : "p-3 sm:p-5",
        "flex flex-col justify-between overflow-hidden flex-1 min-w-0"
      )}>
        <Link href={`/store/${product.id}`} className="flex-1 flex flex-col min-w-0">
          <motion.h3
            layout
            className={cn(
              "font-bold text-white group-hover:text-cyan-400 transition-colors line-clamp-1 leading-tight",
              isList
                ? (isCompact ? "text-xs" : isLarge ? "text-2xl" : "text-base")
                : (isCompact ? "text-xs sm:text-xs" : isLarge ? "text-2xl sm:text-3xl" : "text-sm sm:text-base")
            )}
          >
            {formatSlug(product.name)}
          </motion.h3>

          <motion.div
            layout
            className="flex flex-wrap items-center gap-1.5 mb-3 min-h-[1.5rem] mt-2"
          >
            {product.subItemType && (
              <span className={cn(
                "uppercase tracking-wider bg-white/5 rounded-md font-medium text-muted-foreground border border-white/5 whitespace-nowrap",
                isList
                  ? (isCompact ? "text-[9px] px-1 py-0" : isLarge ? "text-sm px-4 py-1.5" : "text-[10px] px-2 py-0.5")
                  : "text-[10px] px-2 py-0.5"
              )}>
                {formatSlug(product.subItemType)}
              </span>
            )}

            {(product.dimensions?.width ?? 0) > 0 && (
              <span className={cn(
                "uppercase tracking-wider bg-white/5 rounded-md font-medium text-muted-foreground border border-white/5 whitespace-nowrap",
                isList
                  ? (isCompact ? "text-[8px] px-1 py-0" : isLarge ? "text-sm px-4 py-1.5" : "text-[10px] px-2 py-0.5")
                  : "text-[10px] px-2 py-0.5"
              )}>
                {product.dimensions?.width}x{product.dimensions?.height}cm
              </span>
            )}
            {product.collection && !['no-collection', 'none', 'None'].includes(product.collection) && (
              <span className={cn(
                "uppercase tracking-widest font-bold italic whitespace-nowrap",
                isList
                  ? (isCompact ? "text-[7px] text-cyan-400" : isLarge ? "text-sm text-cyan-400" : "text-[10px] text-cyan-400")
                  : "bg-white/5 px-2 py-0.5 rounded-md text-muted-foreground border border-white/5 text-[10px]",
                !isList && isCompact && "hidden sm:inline-block"
              )}>
                {product.collection.replace(/-/g, ' ')}
              </span>
            )}
          </motion.div>

          {isList && product.description && !isCompact && (
            <p className={cn(
              "text-muted-foreground line-clamp-2 hidden sm:block lg:hidden xl:block mt-2",
              isLarge ? "text-base" : "text-sm"
            )}>
              {product.description}
            </p>
          )}
        </Link>

        {/* Price and Add to Cart */}
        <div className="flex items-center justify-between mt-auto gap-3">
          <div className={cn("flex flex-col", isList ? "min-w-[40px]" : "min-w-[60px]")}>
            {product.type === 'artwork' ? (
              <span className={cn(
                "font-bold text-muted-foreground/70 uppercase tracking-tighter italic whitespace-nowrap",
                isCompact ? "text-[8px]" : "text-[10px]"
              )}>
                Unique Art
              </span>
            ) : price ? (
              <span className={cn(
                "font-black text-white whitespace-nowrap",
                isList
                  ? (isCompact ? "text-base" : isLarge ? "text-2xl" : "text-lg")
                  : (isCompact ? "text-xs sm:text-xs" : isLarge ? "text-base sm:text-3xl" : "text-sm sm:text-base")
              )}
              >
                ${Number(price || 0).toFixed(2)}
              </span>
            ) : null}
          </div>

          <div className="flex-1 flex justify-center">
            <span className={cn(
              "uppercase tracking-widest text-cyan-400 bg-cyan-500/10 rounded-full border border-cyan-500/20 whitespace-nowrap shadow-[0_0_10px_rgba(6,182,212,0.1)] font-bold",
              isList
                ? (isLarge ? "text-sm px-5 py-2" : "text-[10px] px-2.5 py-1")
                : "text-[9px] px-2 py-1"
            )}>
              {product.type}
            </span>
          </div>

          <div className={cn("flex items-center", isCompact ? "gap-1" : "gap-2", !isList && "min-w-[60px] justify-end")}>
            <motion.button
              layout
              type="button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (product.type === 'artwork') {
                  setIsOfferModalOpen(true);
                } else {
                  handleAddToCart();
                }
              }}
              className={cn(
                "group/btn inline-flex items-center justify-center transition-all duration-300 border",
                isCompact ? "p-2 rounded-lg" : "px-4 py-2 rounded-xl",
                product.type === 'artwork'
                  ? "bg-cyan-500/10 border-cyan-500/30 hover:bg-gradient-to-r hover:from-purple-600 hover:to-cyan-400 hover:border-transparent"
                  : "bg-white/5 border-white/10 hover:bg-gradient-to-r hover:from-indigo-600 hover:via-pink-500 hover:to-cyan-400 hover:border-white"
              )}
            >
              {product.type === 'artwork' ? (
                <>
                  <Handshake className="w-4 h-4 text-cyan-400 group-hover/btn:text-white transition-colors" />
                  {!isCompact && (
                    <span className="text-cyan-400 font-bold text-sm group-hover/btn:text-white ml-2">
                      Offer
                    </span>
                  )}
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4 text-cyan-400 group-hover/btn:text-white transition-colors" />
                  {!isCompact && (
                    <span className="text-white font-bold text-sm group-hover/btn:text-white ml-2">
                      Add
                    </span>
                  )}
                </>
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {product.type === 'artwork' && (
        <ArtworkOfferModal
          isOpen={isOfferModalOpen}
          onClose={() => setIsOfferModalOpen(false)}
          product={{
            id: product.id,
            name: product.name,
            price: price || 0,
            image: imageUrl,
          }}
          onAcceptOffer={handleAcceptOffer}
        />
      )}
    </motion.div>
  );
}